import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faRobot, faPen, faUpload, faBoxArchive } from '@fortawesome/free-solid-svg-icons';
import {
  archiveBotDefinition,
  createBotDefinition,
  listBotDefinitions,
  publishBotDefinition,
} from '../../../lib/api';
import '../../admin/pages/admin.css';
import '../styles/bot-builder.css';

const BotBuilderList = () => {
  const navigate = useNavigate();
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busyId, setBusyId] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listBotDefinitions();
      setBots(data?.data || []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load bots');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onCreate = async () => {
    setError('');
    setMessage('');
    try {
      const data = await createBotDefinition({ name: 'My WhatsApp bot' });
      const id = data?.data?.id;
      if (id) navigate(`/main/bot-builder/${id}`);
      else await load();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to create bot');
    }
  };

  const onPublish = async (id) => {
    setBusyId(id);
    setError('');
    setMessage('');
    try {
      await publishBotDefinition(id);
      setMessage('Bot published. Customers messaging your number will follow this flow.');
      await load();
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          (e?.response?.data?.errors || []).join(', ') ||
          e?.message ||
          'Publish failed'
      );
    } finally {
      setBusyId('');
    }
  };

  const onArchive = async (id) => {
    if (!window.confirm('Archive this bot?')) return;
    setBusyId(id);
    try {
      await archiveBotDefinition(id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Archive failed');
    } finally {
      setBusyId('');
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page__hero">
        <div className="admin-page__title-row">
          <div className="admin-page__icon" aria-hidden>
            <FontAwesomeIcon icon={faRobot} />
          </div>
          <div>
            <span className="admin-page__eyebrow">Automation</span>
            <h1 className="admin-page__title">Bot Builder</h1>
            <p className="admin-page__subtitle">
              Design WhatsApp conversation flows with drag-and-drop, then publish to your number.
            </p>
          </div>
        </div>
      </header>

      <section className="admin-panel">
        <div className="admin-panel__head" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h2>Your bots</h2>
            <p>Only one published bot runs per WhatsApp number. SourceHub/LensHut static bots stay unchanged.</p>
          </div>
          <button type="button" className="admin-btn admin-btn--primary" onClick={onCreate}>
            <FontAwesomeIcon icon={faPlus} /> New bot
          </button>
        </div>
        <div className="admin-panel__body">
          {error && <div className="admin-feedback admin-feedback--error">{error}</div>}
          {message && <div className="admin-feedback admin-feedback--success">{message}</div>}
          {loading ? (
            <p>Loading…</p>
          ) : bots.length === 0 ? (
            <p className="admin-empty">No bots yet. Create one to open the canvas.</p>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Version</th>
                    <th>Phone number ID</th>
                    <th>Updated</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {bots.map((b) => (
                    <tr key={b.id}>
                      <td>{b.name}</td>
                      <td>
                        <span className={`bot-status bot-status--${b.status}`}>{b.status}</span>
                      </td>
                      <td>{b.version}</td>
                      <td className="admin-cell-clamp">{b.phone_number_id}</td>
                      <td>{b.updated_at ? new Date(b.updated_at).toLocaleString() : '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <Link className="admin-btn admin-btn--ghost admin-btn--sm" to={`/main/bot-builder/${b.id}`}>
                            <FontAwesomeIcon icon={faPen} /> Edit
                          </Link>
                          {b.status !== 'archived' && (
                            <button
                              type="button"
                              className="admin-btn admin-btn--primary admin-btn--sm"
                              disabled={busyId === b.id}
                              onClick={() => onPublish(b.id)}
                            >
                              <FontAwesomeIcon icon={faUpload} /> Publish
                            </button>
                          )}
                          {b.status !== 'archived' && (
                            <button
                              type="button"
                              className="admin-btn admin-btn--ghost admin-btn--sm"
                              disabled={busyId === b.id}
                              onClick={() => onArchive(b.id)}
                            >
                              <FontAwesomeIcon icon={faBoxArchive} /> Archive
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BotBuilderList;
